import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { OrganizationUser } from '@app/v2/shared/types/user.type'
import { USER_STATUS_ACTION } from '../constants/status-dropdown-options'
import { getTeamMemberActionUi, TEAM_MEMBER_API_ACTION, TeamMemberActionPayload } from '../constants/team-member-action'

export const TEAM_MEMBER_ACTION_MODAL_ID = 'team-member-action-modal'

@Component({
  selector: 'app-team-member-action-modal',
  imports: [ModalComponent, NgClass],
  standalone: true,
  templateUrl: './team-member-action-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamMemberActionModalComponent implements OnInit {
  public user = input.required<OrganizationUser>()
  public action = input.required<USER_STATUS_ACTION>()

  public confirmed = output<TeamMemberActionPayload>()
  public closed = output<void>()

  public readonly modalId = TEAM_MEMBER_ACTION_MODAL_ID
  public readonly actionUi = computed(() => getTeamMemberActionUi(this.action()))
  public readonly message = computed(() => {
    const name = this.user().fullname?.trim() || this.user().email
    return this.actionUi()?.message(name) ?? ''
  })
  public readonly actionNoun = computed(() => this.actionUi()?.actionNoun)
  public readonly notifyLabel = computed(() => {
    const noun = this.actionNoun()
    if (!noun) return ''

    const firstName = (this.user().fullname?.trim() || this.user().email).split(/\s+/)[0]
    return `Notify ${firstName} by email about this ${noun}.`
  })
  public readonly notifyByEmail = signal(false)

  private readonly _modalService = inject(ModalService)

  ngOnInit(): void {
    if (!this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
  }

  /**
   * @description Updates the notify-by-email checkbox from its change event
   * @param {Event} event Checkbox change event
   * @returns {void}
   */
  public onNotifyChange(event: Event): void {
    this.notifyByEmail.set((event.target as HTMLInputElement).checked)
  }

  /**
   * @description Emits the confirmed action and closes the modal
   * @returns {void}
   */
  public onConfirm(): void {
    const apiAction = TEAM_MEMBER_API_ACTION[this.action()]
    if (!apiAction) return

    this._closeModal()
    this.confirmed.emit({
      id: this.user().id,
      action: apiAction,
      ...(this.actionNoun() ? { notify: this.notifyByEmail() } : {})
    })
  }

  /**
   * @description Closes the modal
   * @returns {void}
   */
  public onCancel(): void {
    this._closeModal()
    this.closed.emit()
  }

  /**
   * @description Closes the modal if it is currently open
   * @returns {void}
   */
  private _closeModal(): void {
    if (this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
  }
}
